import { push } from 'connected-react-router';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { environment } from 'environments/environment';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { BillingManagementRoute } from 'helpers/routes/modules/billingManagement';
import { buildRoute } from 'helpers/routes/routeBuilder';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { ClaimsGetDataPayload } from 'models/ClaimsCommonPayloads';
import { getApiUrl, performApiCall } from 'saga/apiClient/ducks';
import { getKahlActions } from 'saga/kahl/ducks';
import { selectCurrentData } from 'saga/billingManagement/claims/ducks';
import { Step7ViewVO } from 'generated/bslEbill/claims/claims7/models';

export interface ClaimStep7Payload {
  invoiceNumber: string;
  claimId: number;
  serviceId: number;
  contactEmail: string;
}
/* ACTION TYPES */
export enum ClaimStep7StateTypes {
  GetData = '@@ClaimStep7/GET_DATA',
  ReturnBack = '@@ClaimStep7/RETURN_BACK',
  SendEmailAgain = '@@ClaimStep7/SEND_EMAIL_AGAIN',
  SetLoading = '@@ClaimStep7/SET_LOADING',
}
/* ACTIONS */
export const getClaimStep7DataActions = createApiActionCreators(ClaimStep7StateTypes.GetData);
export const returnBackActions = createApiActionCreators(ClaimStep7StateTypes.ReturnBack);
export const sendEmailAgainActions = createApiActionCreators(ClaimStep7StateTypes.SendEmailAgain);
export const setLoadingAction = createActionCreator(ClaimStep7StateTypes.SetLoading);

/* SAGAS */
function* getData({ payload }: AppAction<ClaimsGetDataPayload>) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.getData,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(getClaimStep7DataActions.success(resp.data));
  } else {
    yield put(getClaimStep7DataActions.failure());
  }
}

function* sendEmailAgain({ payload }: AppAction<ClaimStep7Payload>) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.sendEmailAgain,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.contactEmail,
      payload.serviceId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(
      getKahlActions.request({
        infoMessageIds: 'ID1375',
        exceptionParamMaps: { 'e-mail': payload.contactEmail },
      })
    );
    yield put(sendEmailAgainActions.success());
  } else {
    yield put(sendEmailAgainActions.failure());
  }
}

function* returnBack({ payload }: AppAction<ClaimStep7Payload>) {
  const request: ApiClientRequest = {
    apiCall: call(
      api.returnBack,
      yield getApiUrl(environment.urlConfig.billingManagement),
      payload.invoiceNumber,
      payload.claimId,
      payload.serviceId
    ),
  };
  const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

  if (resp.ok) {
    yield put(returnBackActions.success());

    const step7Data: Step7ViewVO = yield select(selectCurrentData);

    yield put(
      push(
        `${buildRoute(BillingManagementRoute.BillingDetail, { page: 'month-statement-detail' })}?invoiceNumExt=${
          step7Data.invoiceInfo?.invoiceNo
        }&billingCaRefNo=${step7Data.invoiceInfo?.customerAccountNo}`
      )
    );
  } else {
    yield put(getKahlActions.request({ infoMessageIds: 'ID0013' }));
    yield put(returnBackActions.failure());
  }
}

/* EXPORT */
export function* claimStep7Saga() {
  yield takeLatest(createActionType(ClaimStep7StateTypes.GetData, RequestActionTypes.REQUEST), getData);
  yield takeLatest(createActionType(ClaimStep7StateTypes.ReturnBack, RequestActionTypes.REQUEST), returnBack);
  yield takeLatest(createActionType(ClaimStep7StateTypes.SendEmailAgain, RequestActionTypes.REQUEST), sendEmailAgain);
}
